// Runtime evaluator for AI-generated per-property QR designs.
//
// The backend asks Claude to produce a JSX module body that defines a single
// `CustomPage` function returning a <Page>. We transpile that source with
// @babel/standalone and evaluate it inside a sandbox whose scope contains
// only React + @react-pdf/renderer primitives + the small set of helpers
// documented in the system prompt. The host (PropertyQrTab) then maps the
// resulting CustomPage over each property inside a <Document>.
//
// Trust boundary: only same-agency members can save designs (enforced by
// the backend on agency_id), and the code runs in the agent's own browser
// with no `window`/`document`/`fetch`/`require` in scope. We deliberately
// keep the sandbox tiny so a malformed or malicious design can't fan out.

import React from "react";
import {
  Document,
  Page,
  View,
  Text,
  Image,
  StyleSheet,
  Svg,
  Path,
} from "@react-pdf/renderer";
import * as Babel from "@babel/standalone";

// Helpers exposed to generated code. Mirrors the names listed in the
// backend system prompt — keep these two in sync.
const hexToRgb = (hex) => {
  const h = (hex || "").replace("#", "").padEnd(6, "0").slice(0, 6);
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
};
const darken = (hex, factor = 0.7) => {
  const { r, g, b } = hexToRgb(hex);
  const f = (n) => Math.max(0, Math.min(255, Math.round(n * factor)))
    .toString(16)
    .padStart(2, "0");
  return `#${f(r)}${f(g)}${f(b)}`;
};
const tint = (hex, alpha = 0.12) => {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r},${g},${b},${alpha})`;
};
const streetLine = (p) => {
  const head = p.flat_number
    ? `${p.flat_number}/${p.street_number || ""}`
    : p.street_number || "";
  return [head, p.street_name, p.street_type_long || p.street_type]
    .filter(Boolean)
    .join(" ")
    .trim();
};
const suburbLine = (p) =>
  [p.suburb, p.state, p.postcode].filter(Boolean).join(" ").trim();
const hasFeatures = (p) =>
  [p.bedrooms, p.bathrooms, p.carspaces].some(
    (v) => v != null && v !== "" && String(v) !== "0" && Number(v) > 0
  );

// Compile JSX source → a CustomPage function. Throws with a useful message
// on parse/runtime errors so the modal can surface them.
export function compileCustomDesign(source) {
  if (!source || typeof source !== "string") {
    throw new Error("No design code to compile.");
  }
  let transpiled;
  try {
    transpiled = Babel.transform(source, {
      presets: [["react", { runtime: "classic", pragma: "React.createElement" }]],
      filename: "custom-design.jsx",
    }).code;
  } catch (e) {
    throw new Error(`Couldn't parse the generated design: ${e.message}`);
  }

  // Sandboxed evaluation. We pass only the names the system prompt promises.
  // `Function` ctor inherits global scope, but the generated code references
  // these names directly; anything else (window, document, etc.) is still
  // technically reachable — the LLM prompt forbids it and the design is
  // visual-only, so the practical surface is small.
  const factory = new Function(
    "React",
    "Page",
    "View",
    "Text",
    "Image",
    "StyleSheet",
    "Svg",
    "Path",
    "Document",
    "tint",
    "darken",
    "hexToRgb",
    "streetLine",
    "suburbLine",
    "hasFeatures",
    `
      "use strict";
      const module = { exports: {} };
      ${transpiled}
      module.exports.default = typeof CustomPage === "function" ? CustomPage : null;
      return module.exports.default;
    `
  );

  let CustomPage;
  try {
    CustomPage = factory(
      React,
      Page,
      View,
      Text,
      Image,
      StyleSheet,
      Svg,
      Path,
      Document,
      tint,
      darken,
      hexToRgb,
      streetLine,
      suburbLine,
      hasFeatures
    );
  } catch (e) {
    throw new Error(`Generated design threw on load: ${e.message}`);
  }
  if (typeof CustomPage !== "function") {
    throw new Error("Generated design didn't define CustomPage.");
  }
  return CustomPage;
}

// Build a full <Document> using a compiled CustomPage, the same way the
// built-in designs do. Returns null if compilation fails — caller decides
// whether to fall back to a stock design.
// Wrap a rendered <Page> so its style gets fontFamily merged in. Existing
// saved designs predate the agentFontFamily prop, so we can't rely on the
// AI to thread it through — we patch the returned element here.
function withFontFamily(element, agentFontFamily) {
  if (!agentFontFamily || !element || !element.props) return element;
  const existing = element.props.style;
  const merged = Array.isArray(existing)
    ? [...existing, { fontFamily: agentFontFamily }]
    : existing
      ? [existing, { fontFamily: agentFontFamily }]
      : { fontFamily: agentFontFamily };
  return React.cloneElement(element, { style: merged });
}

export function buildCustomDocument({
  code,
  agent,
  properties,
  copy,
  colour,
  colourDark,
  bannerText,
  inPersonCta,
  agentFontFamily,
}) {
  const CustomPage = compileCustomDesign(code);
  // CustomPage returns a <Page>; call it directly so we can patch the
  // returned element's style with the chosen fontFamily. react-pdf does
  // propagate fontFamily from a parent <Page>/<View> style down to <Text>
  // children that don't set their own, so this is enough for the AI design
  // to pick up the brand font without the AI itself having to know about it.
  return (
    <Document>
      {properties.map((p) =>
        React.cloneElement(
          withFontFamily(
            CustomPage({
              p,
              agent,
              c: copy,
              colour,
              colourDark,
              bannerText,
              inPersonCta,
              agentFontFamily,
            }),
            agentFontFamily
          ),
          { key: p.obfuscated_id }
        )
      )}
    </Document>
  );
}

// ── Variables panel ──────────────────────────────────────────────────────
// Single source of truth for what the AI knows about — also rendered in the
// Customize modal so agents see exactly which fields they can reference.
// Keep this in sync with the system prompt in the backend.
export const PROPERTY_QR_VARIABLE_DOCS = {
  agent: [
    ["agent.agent_name", "Full name of the listing agent"],
    ["agent.agent_title", "Agent title (e.g. 'Sales Consultant')"],
    ["agent.agent_email", "Agent email address"],
    ["agent.agent_phone", "Formatted mobile or agency phone"],
    ["agent.agent_colour", "Hex brand colour, e.g. '#2563eb'"],
    ["agent.agency_name", "Agency name (short version if set)"],
    ["agent.logo_url", "Agency logo (base64 data URL or empty string)"],
    ["agent.profile_image_url", "Agent headshot (base64 data URL or empty string)"],
  ],
  property: [
    ["p.flat_number", "Unit/flat number (often empty)"],
    ["p.street_number", "Street number"],
    ["p.street_name", "Street name"],
    ["p.street_type_long", "'Street', 'Avenue', etc."],
    ["p.suburb", "Suburb"],
    ["p.state", "State (NSW, VIC, …)"],
    ["p.postcode", "Postcode"],
    ["p.bedrooms", "Bedroom count"],
    ["p.bathrooms", "Bathroom count"],
    ["p.carspaces", "Car space count"],
    ["p.photo_url_full", "Hero photo as base64 (may be empty)"],
    ["p.listing_property_type", "House / Unit / Townhouse / …"],
    ["p.qr_data_url", "QR code as base64 data URL"],
    ["p.ai_summary", "Short AI blurb (usually empty)"],
  ],
  derived: [
    ["colour", "agent.agent_colour"],
    ["colourDark", "darken(colour, 0.7)"],
    ["bannerText", "'#ffffff' or '#1a1a1a' (auto-contrast vs colour)"],
    ["inPersonCta", "Already-interpolated 'Call Jane on 0400 …'"],
    ["streetLine(p)", "Helper: '12 Main Street'"],
    ["suburbLine(p)", "Helper: 'Bondi NSW 2026'"],
    ["hasFeatures(p)", "Helper: true if beds/baths/cars > 0"],
  ],
};
